import { Box, Text, Button, Input, FormControl, FormErrorMessage, VStack, HStack, Checkbox, Flex, Icon, Image, InputGroup, InputLeftElement, InputRightElement, Stack } from '@chakra-ui/react';
import { CheckCircleFilled, CheckCircleOutlined } from '@ant-design/icons';
import StepHeader from './StepHeader';
import { nextStep } from '../../store/formSlice';
import { useDispatch, useSelector } from 'react-redux';
import CardIcon from '../../assets/CardIcon.png';
import CircleTick from '../../assets/CircleTick.png';
import { Elements, CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from "@stripe/react-stripe-js";
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { addpaymentmethod, createtrialsubscription } from '../../collection/ApiCollection';
import { toast } from 'react-toastify';
import { CardLogo } from '../../utils/Images';

const STRIPE_PUBLISHABLE_KEY = "pk_test_51QKnOZK0DzdlOO6IoRy2ZXy6Wnl17P9LRM2p177Grh6BApETVMef2kgthqfGJTXD8xOfROUMmySTtvV34GlDdxcP00ccf2R2Wi";
let stripePromise = null;
const getStripePromise = () => {
    if (!stripePromise) {
        stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);
    }
    return stripePromise;
};

const elementStyles = {
    style: {
        base: {
            fontSize: '16px',
            color: '#424770',
            '::placeholder': {
                color: '#aab7c4',
            },
        },
        invalid: {
            color: '#9e2146',
        },
    },
};

// Validation and utility functions outside the component
const validateCardForm = ({ cardOwner, setDefault, cardComplete, expiryComplete, cvvComplete }) => {
    let valid = true;
    let newErrors = {
        cardOwner: '',
        setDefault: '',
        cardNumber: '',
        expiry: '',
        cvv: ''
    };
    if (!cardOwner.trim()) {
        newErrors.cardOwner = 'Card owner is required';
        valid = false;
    }
    if (!setDefault) {
        newErrors.setDefault = 'Please set as default';
        valid = false;
    }
    if (!cardComplete) {
        newErrors.cardNumber = 'Please enter a valid card number';
        valid = false;
    }
    if (!expiryComplete) {
        newErrors.expiry = 'Please enter a valid expiry date';
        valid = false;
    }
    if (!cvvComplete) {
        newErrors.cvv = 'Please enter a valid CVV';
        valid = false;
    }
    return { valid, newErrors };
};

const CardForm = () => {
    const [cardOwner, setCardOwner] = useState('');
    const [setDefault, setSetDefault] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({
        cardOwner: '',
        setDefault: '',
        cardNumber: '',
        expiry: '',
        cvv: ''
    });
    const [cardComplete, setCardComplete] = useState(false);
    const [expiryComplete, setExpiryComplete] = useState(false);
    const [cvvComplete, setCvvComplete] = useState(false);
    const dispatch = useDispatch();
    const stripe = useStripe();
    const elements = useElements();

    const handleChange = useCallback((setter, errorKey) => (e) => {
        setter(e.target.value);
        if (errors[errorKey]) setErrors((prev) => ({ ...prev, [errorKey]: '' }));
    }, [errors]);

    const handleCheckbox = useCallback((e) => {
        setSetDefault(e.target.checked);
        if (errors.setDefault) setErrors((prev) => ({ ...prev, setDefault: '' }));
    }, [errors.setDefault]);

    // Handle Stripe Element changes
    const handleCardNumberChange = useCallback((event) => {
        if (event.error) {
            setErrors(prev => ({ ...prev, cardNumber: event.error.message }));
        } else {
            setErrors(prev => ({ ...prev, cardNumber: '' }));
        }
        setCardComplete(event.complete);
    }, []);

    const handleExpiryChange = useCallback((event) => {
        if (event.error) {
            setErrors(prev => ({ ...prev, expiry: event.error.message }));
        } else {
            setErrors(prev => ({ ...prev, expiry: '' }));
        }
        setExpiryComplete(event.complete);
    }, []);

    const handleCvvChange = useCallback((event) => {
        if (event.error) {
            setErrors(prev => ({ ...prev, cvv: event.error.message }));
        } else {
            setErrors(prev => ({ ...prev, cvv: '' }));
        }
        setCvvComplete(event.complete);
    }, []);

    const validate = useCallback(() => {
        const { valid, newErrors } = validateCardForm({ cardOwner, setDefault, cardComplete, expiryComplete, cvvComplete });
        setErrors(newErrors);
        return valid;
    }, [cardOwner, setDefault, cardComplete, expiryComplete, cvvComplete]);

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        if (!validate()) {
            return;
        }
        if (!stripe || !elements) {
            toast.error("Stripe.js has not loaded.");
            return;
        }
        const cardElement = elements.getElement(CardNumberElement);
        if (!cardElement) {
            toast.error("Card element not found.");
            return;
        }
        setLoading(true);
        try {
            const { token, error } = await stripe.createToken(cardElement, {
                name: cardOwner,
            });
            if (error) {
                toast.error(error.message || "Failed to create token.");
                setLoading(false);
                return;
            }
            // Call the addpaymentmethod API
            const response = await addpaymentmethod({
                paymentMethodToken: token.id
            });
            if (response?.status === 200) {
                toast.success(response?.message || "Payment method added successfully!");
                let res = await createtrialsubscription({ type: "1" });
                if (res?.status === 200) {
                    dispatch(nextStep());
                } else {
                    toast.error('Something went wrong.')
                }
            } else {
                toast.error(response?.response?.data?.message || response?.error || response?.message || "Something went wrong");
            }
        } catch (err) {
            console.error("Error during submission:", err);
            toast.error("An unexpected error occurred.");
        } finally {
            setLoading(false);
        }
    }, [stripe, elements, cardOwner, validate, dispatch]);

    return (
        <Box maxW={{ base: '100%', md: '85%' }} mx="auto" p={{ base: 4, md: 6 }} textAlign="center">
            <StepHeader
                title="You are one step away from your 30 days free trail"
                subtitle="We will only charge you after 30 days free trail"
            />
            <Box bg="#F6FBFD" borderRadius="16px" p={{ base: 4, md: 8 }} maxW="xl" mx="auto" mb={8} boxShadow="md" position="relative">
                <Flex justify="space-between" align="center" mb={4}>
                    <HStack spacing={2} align="center">
                        <Icon as={CheckCircleOutlined} color="green.400" boxSize={6} />
                        <Text fontWeight="bold" fontSize={{ base: '16px', md: '18px' }} mb="5px">Add new card</Text>
                    </HStack>
                    <HStack spacing={3}>
                        <Image src={CardLogo} alt="Mastercard" w={["108px", "142px"]} h={["22px", "28px"]} />
                    </HStack>
                </Flex>
                <form onSubmit={handleSubmit}>
                    <VStack spacing={6} align="stretch">
                        <FormControl isInvalid={!!errors.cardNumber}>
                            <Stack direction={['column', 'row']} spacing="12px">
                                <VStack spacing="5px" align="flex-start" w="80%">
                                    <Text fontWeight="bold" fontSize="14px" m="0">Card number</Text>
                                    <Text fontSize="10px" mb={1} textAlign="left">Enter the 16-digit card number on the card</Text>
                                </VStack>
                                <InputGroup>
                                    <InputLeftElement pointerEvents="none">
                                        <Image src={CardIcon} alt="Card Icon" boxSize="20px" ml={2} />
                                    </InputLeftElement>
                                    <Box
                                        w="100%"
                                        p={3}
                                        border="1px solid"
                                        borderColor={errors.cardNumber ? "red.500" : "brand.500"}
                                        borderRadius="md"
                                        bg="white"
                                        pl={10}
                                    >
                                        <CardNumberElement
                                            options={elementStyles}
                                            onChange={handleCardNumberChange}
                                        />
                                    </Box>
                                    <InputRightElement pointerEvents="none">
                                        <CheckCircleFilled />
                                    </InputRightElement>
                                </InputGroup>
                            </Stack>
                            <FormErrorMessage>{errors.cardNumber}</FormErrorMessage>
                        </FormControl>
                        <FormControl isInvalid={!!errors.cardOwner}>
                            <Stack spacing="12px" direction={['column', 'row']}>
                                <VStack spacing="5px" align="flex-start" w="80%">
                                    <Text fontWeight="bold" fontSize="14px" m="0">Card owner</Text>
                                    <Text fontSize="10px" mb={1} textAlign="left">Enter the name on the card</Text>
                                </VStack>
                                <Input
                                    id="cardOwner"
                                    name="cardOwner"
                                    placeholder=""
                                    value={cardOwner}
                                    borderColor="brand.500"
                                    onChange={handleChange(setCardOwner, 'cardOwner')}
                                    maxW="100%"
                                    ml={[null, 4]}
                                />
                            </Stack>
                            <FormErrorMessage>{errors.cardOwner}</FormErrorMessage>
                        </FormControl>
                        <FormControl isInvalid={!!errors.expiry || !!errors.cvv}>
                            <Stack direction={['column', 'row']} spacing="12px">
                                <VStack spacing="5px" align="flex-start" w="80%">
                                    <Text fontWeight="bold" fontSize="14px" m="0">Expiry date</Text>
                                    <Text fontSize="10px" mb={1} textAlign="left">Enter the expiration date of the card</Text>
                                </VStack>
                                <HStack spacing="8px" minW="54%">
                                    <Box
                                        w="140px"
                                        p={3}
                                        border="1px solid"
                                        borderColor={errors.expiry ? "red.500" : "brand.500"}
                                        borderRadius="md"
                                        bg="white"
                                    >
                                        <CardExpiryElement
                                            options={{ ...elementStyles, placeholder: "MM/YY" }}
                                            onChange={handleExpiryChange}
                                        />
                                    </Box>
                                    <HStack>
                                        <VStack spacing="5px" align="flex-start" w="80%">
                                            <Text fontWeight="bold" fontSize="14px" m="0">CVV</Text>
                                            <Text fontSize="10px" mb={1} textAlign="left" minW="62px">Security code</Text>
                                        </VStack>
                                        <Box
                                            w="80px"
                                            p={3}
                                            border="1px solid"
                                            borderColor={errors.cvv ? "red.500" : "brand.500"}
                                            borderRadius="md"
                                            bg="white"
                                        >
                                            <CardCvcElement
                                                options={{ ...elementStyles, placeholder: "CVC" }}
                                                onChange={handleCvvChange}
                                            />
                                        </Box>
                                    </HStack>
                                </HStack>
                            </Stack>
                            {(errors.expiry || errors.cvv) && (
                                <FormErrorMessage>
                                    {errors.expiry || errors.cvv}
                                </FormErrorMessage>
                            )}
                        </FormControl>
                        <Flex justify="flex-end" mt={2}>
                            <Checkbox
                                id="setDefault"
                                name="setDefault"
                                isChecked={setDefault}
                                onChange={handleCheckbox}
                                colorScheme="brand"
                            >
                                Set us default
                            </Checkbox>
                        </Flex>
                        {errors.setDefault && (
                            <Text color="red.500" fontSize="12px" textAlign="right">
                                {errors.setDefault}
                            </Text>
                        )}
                    </VStack>
                    <Button
                        type="submit"
                        mt={4}
                        px="30px"
                        isLoading={loading}
                        loadingText="Processing..."
                        isDisabled={!cardOwner.trim() || !setDefault || !cardComplete || !expiryComplete || !cvvComplete}
                    >
                        {loading ? "Processing..." : "Done"}
                    </Button>
                </form>
            </Box>
        </Box>
    );
};

const AddPaymentStep = () => {
    // Get the stable Stripe promise
    const stableStripePromise = useMemo(() => getStripePromise(), []);

    // Memoize the Elements component to prevent prop changes
    const elementsComponent = useMemo(() => (
        <Elements stripe={stableStripePromise}>
            <CardForm />
        </Elements>
    ), [stableStripePromise]);

    return elementsComponent;
};

export default AddPaymentStep; 